import React, { useState, useEffect, ChangeEvent, useRef, useMemo } from "react";
import styles from "./TeamUpdate.module.css";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_ALL_LEAGUES,
  GET_TEAMS_BY_LEAGUE,
  GET_TEAM_CATEGORIES,
  GET_TEAM_DETAILS,
  UPDATE_TEAM_DETAILS,
  UPDATE_TEAM_ALTERNATE_NAMES,
  UPDATE_TEAM_BRAND_MARKET,
  UPDATE_TEAM_CONFERENCE,
  GET_LEAGUE_CONFERENCES,
  GET_ALL_CONFERENCES,
  ADD_LEAGUE_CONFERENCE,
  UPDATE_LEAGUE_CONFERENCE,
  DELETE_LEAGUE_CONFERENCE,
  INSERT_LEAGUE_TEAM,
  DELETE_LEAGUE_TEAM,
  DELETE_LEAGUE_TEAM_VENUES,
  DELETE_LEAGUE_ALTERNATIVE_TEAMS,
  SOFT_DELETE_LEAGUE_CATEGORY,
  GET_TEAM_CONFERENCE,
  UPDATE_LEAGUE_TEAM_CONFERENCE,
  GET_TEAMS_WITH_CONFERENCE,
  GET_TEAM_CONFERENCE_DETAILS,
  INSERT_LEAGUE_CATEGORY,
  GET_ALL_CONFERENCES_WITH_DELETED,
} from "./api/TeamUpdateQueries";
import { CircularProgress } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { v4 as uuidv4 } from "uuid";

interface League {
  l_id: string;
  l_name: string;
  l_type: string;
}

interface Team {
  lt_id: string;
  lt_name: string;
  lt_color_code?: string;
  lt_abbreviation?: string;
  lt_category_ref_id?: string | null;
  l_id?: string;
}

interface TeamDetails {
  lc_id: string;
  lc_name: string;
  lc_alternate_name: string[];
  lc_brand_name: string;
  lc_market_name: string;
  lcf_id?: string;
}

interface TeamData {
  league: League | null;
  team: Team | null;
  alternateNames: string[];
  brandName: string;
  marketName: string;
  newAlternateName: string;
  newBrandName: string;
  newMarketName: string;
}

interface AddTeamForm {
  league: League | null;
  teamName: string;
  teamColor: string;
  abbreviation: string;
  conference: string;
}

interface Conference {
  lcf_id: string;
  l_id: string;
  lcf_name: string;
  lcf_display_order: number;
  lcf_deleted_at?: string | null;
}

const isNcaaLeague = (league: League | null): boolean => {
  if (!league) return false;
  return (
    league.l_type?.toLowerCase().includes("ncaa") ||
    league.l_name?.toLowerCase().includes("ncaa")
  );
};

const TeamUpdate: React.FC = () => {
  const hasuraRole = (localStorage.getItem("x-hasura-user-role") || "").toLowerCase();
  const canEditConferences =
    hasuraRole === "admin" ||
    hasuraRole === "super_user" ||
    hasuraRole === "sub_admin" ||
    hasuraRole === "affiliater_admin" ||
    hasuraRole === "marketing_user";

  const [leagues, setLeagues] = useState<League[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamDetails, setTeamDetails] = useState<TeamDetails | null>(null);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [addTeamForm, setAddTeamForm] = useState<AddTeamForm>({
    league: null,
    teamName: "",
    teamColor: "#6366f1",
    abbreviation: "",
    conference: "",
  });
  const [activeTab, setActiveTab] = useState<"categories" | "conferences" | "team-assignment">("categories");

  // Category tab conference state
  const [leagueConferences, setLeagueConferences] = useState<Conference[]>([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState<string>("");
  const [loadingConferences, setLoadingConferences] = useState(false);
  const [currentConferenceName, setCurrentConferenceName] = useState<string>("");
  const [teamConferenceId, setTeamConferenceId] = useState<string>("");

  // Conferences tab state
  const [conferences, setConferences] = useState<Conference[]>([]);
  const [editingConfId, setEditingConfId] = useState<string | null>(null);
  const [editingConfName, setEditingConfName] = useState("");
  const [editingConfLeagues, setEditingConfLeagues] = useState<string[]>([]);
  const [originalLeaguesForEditing, setOriginalLeaguesForEditing] = useState<string[]>([]);
  const [showAddConfForm, setShowAddConfForm] = useState(false);
  const [newConfName, setNewConfName] = useState("");
  const [newConfOrder, setNewConfOrder] = useState<number>(1);
  const [newConfLeagueId, setNewConfLeagueId] = useState<string>("");
  const [confLoading, setConfLoading] = useState(false);
  const [addTeamConferences, setAddTeamConferences] = useState<Conference[]>([]);
  const [addTeamConfLoading, setAddTeamConfLoading] = useState(false);
  const [copyingConf, setCopyingConf] = useState(false);
  const [isTeamsFallbackMode, setIsTeamsFallbackMode] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterLeagueId, setFilterLeagueId] = useState("");

  // Team Assignment Tab State
  const [assignmentLeague, setAssignmentLeague] = useState<League | null>(null);
  const [assignmentTeams, setAssignmentTeams] = useState<any[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentConferences, setAssignmentConferences] = useState<Conference[]>([]);
  const [selectedTeamForAssignment, setSelectedTeamForAssignment] = useState<any | null>(null);
  const [selectedConferenceForAssignment, setSelectedConferenceForAssignment] = useState<string>("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [teamDetailsLoading, setTeamDetailsLoading] = useState(false);
  const [teamData, setTeamData] = useState<TeamData>({
    league: null,
    team: null,
    alternateNames: [],
    brandName: "",
    marketName: "",
    newAlternateName: "",
    newBrandName: "",
    newMarketName: "",
  });
  const [openLeagueDropdownFor, setOpenLeagueDropdownFor] = useState<string | null>(null);
  const [isAddLeagueDropdownOpen, setIsAddLeagueDropdownOpen] = useState(false);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark" || document.documentElement.getAttribute("data-theme") === "dark";
  });

  useEffect(() => {
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
    document.documentElement.setAttribute("data-theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const isUpdatingRef = useRef(false);
  const leaguesFetched = useRef(false);
  const confTabFetched = useRef(false);
const [teamSearchTerm, setTeamSearchTerm] = useState("");
const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
const teamDropdownRef = useRef<HTMLDivElement>(null);
const [leagueSearchTerm, setLeagueSearchTerm] = useState("");
const [isLeagueDropdownOpen, setIsLeagueDropdownOpen] = useState(false);
const leagueDropdownRef = useRef<HTMLDivElement>(null);
  // GraphQL queries & mutations
  // For Categories tab conference dropdown
const [confSearchTerm, setConfSearchTerm] = useState("");
const [isConfDropdownOpen, setIsConfDropdownOpen] = useState(false);
const confDropdownRef = useRef<HTMLDivElement>(null);

// For Add Team modal conference dropdown
const [addTeamConfSearchTerm, setAddTeamConfSearchTerm] = useState("");
const [isAddTeamConfDropdownOpen, setIsAddTeamConfDropdownOpen] = useState(false);
const addTeamConfDropdownRef = useRef<HTMLDivElement>(null);
  const [getAllLeagues] = useLazyQuery(GET_ALL_LEAGUES, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setLeagues(data?.leagues || []);
      if (data?.leagues?.length) {
        setTeamData((prev) => ({ ...prev, league: data.leagues[0] }));
      }
      setLoading(false);
    },
    onError: () => {
      toast.error("Error fetching leagues", { toastId: "fetch-leagues-error" });
      setLoading(false);
    },
  });

  const filteredTeams = useMemo(() => {
  if (!teamSearchTerm.trim()) return teams;
  return teams.filter(team =>
    team.lt_name.toLowerCase().includes(teamSearchTerm.toLowerCase())
  );
}, [teams, teamSearchTerm]);
const filteredLeagues = useMemo(() => {
  if (!leagueSearchTerm.trim()) return leagues;
  return leagues.filter(league =>
    league.l_name.toLowerCase().includes(leagueSearchTerm.toLowerCase())
  );
}, [leagues, leagueSearchTerm]);
  const [getTeams] = useLazyQuery(GET_TEAMS_BY_LEAGUE, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setTeams(data?.league_teams || []);
      if (data?.league_teams?.length) {
        const firstTeam = data.league_teams[0];
        setTeamData((prev) => ({ ...prev, team: firstTeam }));
      } else {
        setTeamData((prev) => ({ ...prev, team: null }));
        setTeamDetails(null);
      }
    },
    onError: () => {
      toast.error("Error fetching teams", { toastId: "fetch-teams-error" });
    },
  });

  const [getTeamCategories] = useLazyQuery(GET_TEAM_CATEGORIES, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.league_categories?.[0]) {
        const details = data.league_categories[0];
        setTeamDetails(details);
        setTeamData((prev) => ({
          ...prev,
          alternateNames: details.lc_alternate_name || [],
          brandName: details.lc_brand_name || "",
          marketName: details.lc_market_name || "",
          newBrandName: "",
          newMarketName: "",
          newAlternateName: "",
        }));
        setSelectedConferenceId(details.lcf_id || "");
      }
    },
    onError: () => toast.error("Error fetching team categories", { toastId: "fetch-categories-error" }),
  });

  const [getTeamDetails] = useLazyQuery(GET_TEAM_DETAILS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const details = data?.league_categories?.[0];
      console.log("Team details received:", details);
      
      if (details && details.lc_id) {
        setTeamDetails(details);
        setTeamData((prev) => ({
          ...prev,
          alternateNames: details.lc_alternate_name || [],
          brandName: details.lc_brand_name || "",
          marketName: details.lc_market_name || "",
        }));
        setSelectedConferenceId(details.lcf_id || "");
        setTeamDetailsLoading(false);
      } else {
        console.warn("No team details found or missing lc_id");
        setTeamDetails(null);
        setTeamData((prev) => ({
          ...prev,
          alternateNames: [],
          brandName: "",
          marketName: "",
        }));
        setSelectedConferenceId("");
        setTeamDetailsLoading(false);
        
        // If no details found, maybe we need to initialize?
        // if (teamData.team?.lt_id) {
        //   toast.info("No existing details found for this team. You can add new details.", { toastId: "no-details" });
        // }
      }
    },
    onError: (err) => {
      console.error("❌ Error fetching team details:", err);
      toast.error("Error loading team details: " + err.message, { toastId: "fetch-team-details-error" });
      setTeamDetailsLoading(false);
      setTeamDetails(null);
    }
  });

  const [insertLeagueCategory] = useMutation(INSERT_LEAGUE_CATEGORY);

  const handleAddTeamSubmit = async () => {
    try {
      if (!addTeamForm.league || !addTeamForm.teamName) {
        toast.error("League and Team Name are required", { toastId: "add-team-required" });
        return;
      }

      const leagueId = addTeamForm.league.l_id;
      const teamName = addTeamForm.teamName.trim();
      const abbreviation = addTeamForm.abbreviation || null;
      const color = addTeamForm.teamColor || null;

      // NCAA → conference required
      const isNcaa = isNcaaLeague(addTeamForm.league);
      const conferenceId = isNcaa ? addTeamForm.conference || null : null;

      if (isNcaa && !conferenceId) {
        toast.error("Conference is required for NCAA leagues", { toastId: "ncaa-conf-required" });
        return;
      }

      // If selected conference belongs to another league, copy it into the selected league first
      let effectiveConferenceId = conferenceId;
      if (isNcaa && conferenceId) {
        const selectedConf = addTeamConferences.find((c) => c.lcf_id === conferenceId);
        if (selectedConf && selectedConf.l_id !== leagueId) {
          setCopyingConf(true);
          try {
            const { data: copyData } = await copyConferenceMutation({
              variables: {
                l_id: leagueId,
                lcf_name: selectedConf.lcf_name,
                lcf_display_order: selectedConf.lcf_display_order || 1,
              },
            });
            effectiveConferenceId = copyData?.insert_league_conferences_one?.lcf_id || conferenceId;
          } finally {
            setCopyingConf(false);
          }
        }
      }

      // 🔥 STEP 1: Generate SAME category_ref_id
      const categoryRefId = uuidv4();

      // 🔥 STEP 2: Insert Category FIRST
      await insertLeagueCategory({
        variables: {
          l_id: leagueId,
          lc_name: teamName,
          lc_category_ref_id: categoryRefId,
          lcf_id: effectiveConferenceId,
          lc_colour_code: color,
          lc_abbrev: abbreviation,
          lc_ticketmaster_name: teamName,
        },
      });

      // 🔥 STEP 3: Insert Team with SAME ref id
      await insertLeagueTeam({
        variables: {
          l_id: leagueId,
          lt_name: teamName,
          lt_abbreviation: abbreviation,
          lt_color_code: color,
          lt_category_ref_id: categoryRefId,
        },
      });


      handleAddTeamReset();
      setShowAddTeamModal(false);

    } catch (err) {
      console.error(err);
      toast.error("❌ Failed to add team", { toastId: "add-team-error" });
    }
  };
  
  const [getTeamConference] = useLazyQuery(GET_TEAM_CONFERENCE, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.league_categories?.[0]?.lcf_id) {
        const confId = data.league_categories[0].lcf_id;
        setTeamConferenceId(confId);
        setSelectedConferenceId(confId);
        const conf = leagueConferences.find(c => c.lcf_id === confId);
        setCurrentConferenceName(conf?.lcf_name || "");
      } else {
        setTeamConferenceId("");
        setSelectedConferenceId("");
        setCurrentConferenceName("");
      }
    },
    onError: () => {
      toast.error("Error fetching team conference", { toastId: "fetch-team-conf-error" });
      setTeamConferenceId("");
      setSelectedConferenceId("");
      setCurrentConferenceName("");
    },
  });

  const [getLeagueConferencesQuery] = useLazyQuery(GET_LEAGUE_CONFERENCES, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const conferenceList = data?.league_conferences || [];
      setConferences(conferenceList);
      setLeagueConferences(conferenceList);
      setConfLoading(false);
      setLoadingConferences(false);
      if (teamConferenceId) {
        const conf = conferenceList.find((c: { lcf_id: string; }) => c.lcf_id === teamConferenceId);
        setCurrentConferenceName(conf?.lcf_name || "");
        setSelectedConferenceId(teamConferenceId);
      }
    },
    onError: (err) => {
      toast.error("Error fetching conferences: " + err.message, { toastId: "fetch-conf-error-static" });
      setLoadingConferences(false);
    },
  });

  const [getAllConferencesForTab] = useLazyQuery(GET_ALL_CONFERENCES_WITH_DELETED, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setConferences(data?.league_conferences || []);
      setConfLoading(false);
    },
    onError: (err) => toast.error("Error fetching conferences: " + err.message, { toastId: "fetch-all-conf-error-static" }),
  });

  const getConferences = () => {
    setConfLoading(true);
    getAllConferencesForTab();
  };

  const [addConferenceMutation] = useMutation(ADD_LEAGUE_CONFERENCE, {
    onCompleted: () => {
      setShowAddConfForm(false);
      setNewConfName("");
      setNewConfLeagueId("");
      setNewConfOrder(1);
      getConferences();
    },
    onError: (err) => {
      const message = err.message || "Error adding conference";
      if (message.toLowerCase().includes("duplicate key") || message.toLowerCase().includes("unique constraint")) {
        toast.error("Conference already exists for this league.", { toastId: "conf-add-duplicate-error" });
      } else {
        toast.error("Error adding conference: " + message, { toastId: "conf-add-error-static" });
      }
    },
  });

  const [getAddTeamConferences] = useLazyQuery(GET_ALL_CONFERENCES, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setAddTeamConferences(data?.league_conferences || []);
      setAddTeamConfLoading(false);
    },
    onError: () => {
      toast.error("Error fetching conferences for add team", { toastId: "add-team-conf-fetch-error" });
      setAddTeamConferences([]);
      setAddTeamConfLoading(false);
    },
  });

  const [copyConferenceMutation] = useMutation(ADD_LEAGUE_CONFERENCE, {
    onCompleted: (data) => {
      toast.success(`Conference "${data.insert_league_conferences_one.lcf_name}" added to ${addTeamForm.league?.l_name}!`, { toastId: "copy-conf-success" });
      setCopyingConf(false);
      const newId = data.insert_league_conferences_one.lcf_id;
      setAddTeamForm((prev) => ({ ...prev, conference: newId }));
      getAddTeamConferences();
      getConferences();
    },
    onError: (err) => {
      setCopyingConf(false);
      const message = err.message || "Error copying conference";
      if (message.toLowerCase().includes("duplicate key") || message.toLowerCase().includes("unique constraint")) {
        toast.error("Conference already exists for this league.", { toastId: "copy-conf-duplicate-error" });
      } else {
        toast.error("Error copying conference: " + message, { toastId: "copy-conf-error-static" });
      }
    },
  });

  const [updateConferenceMutation] = useMutation(UPDATE_LEAGUE_CONFERENCE, {
    onCompleted: () => {
      toast.success("Conference updated!", { toastId: "conf-update-success" });
      getConferences();
    },
    onError: (err) => toast.error("Error updating conference: " + err.message, { toastId: "conf-update-error-static" }),
  });

 const [deleteConferenceMutation] = useMutation(DELETE_LEAGUE_CONFERENCE, {
  onCompleted: () => {
    getConferences();
  },
  onError: (err) => {
    if (err.message.includes("foreign key") || err.message.includes("violates")) {
      toast.error("Cannot delete conference: It is being used by teams. Please reassign teams first.", { 
        toastId: "conf-delete-fk-error" 
      });
    } else {
      toast.error("Error removing conference: " + err.message, { toastId: "conf-delete-error-static" });
    }
  },
});

  const [insertLeagueTeam] = useMutation(INSERT_LEAGUE_TEAM, {
    onCompleted: (data) => {
      toast.success(`Team "${data.insert_league_teams_one.lt_name}" added successfully!`, { toastId: "add-team-success" });
      setShowAddTeamModal(false);
      handleAddTeamReset();
      const newLeagueId = data?.insert_league_teams_one?.l_id || addTeamForm.league?.l_id;
      const insertedTeam = data?.insert_league_teams_one;
      if (newLeagueId) {
        // Always refresh the teams list for the league where the team was added
        getTeams({ variables: { l_id: newLeagueId } });

        // If the main form is currently viewing the same league, select the newly added team
        if (teamData.league?.l_id === newLeagueId && insertedTeam) {
          setTeamData((prev) => ({
            ...prev,
            team: {
              lt_id: insertedTeam.lt_id,
              lt_name: insertedTeam.lt_name,
              lt_color_code: insertedTeam.lt_color_code || prev.team?.lt_color_code,
              lt_category_ref_id: insertedTeam.lt_category_ref_id || prev.team?.lt_category_ref_id,
              l_id: newLeagueId,
            },
          }));
        }
      }
    },
    onError: (err) => {
      if (err.message.includes("league_teams_l_id_lt_name_key") || err.message.includes("Uniqueness violation")) {
        toast.error("A team with this name already exists in the selected league.", { toastId: "add-team-duplicate-error" });
      } else {
        toast.error("Error adding team: " + err.message, { toastId: "add-team-error-static" });
      }
    },
  });

  useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (leagueDropdownRef.current && !leagueDropdownRef.current.contains(event.target as Node)) {
      setIsLeagueDropdownOpen(false);
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target as Node)) {
      setIsTeamDropdownOpen(false);
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);
  const [deleteLeagueTeam] = useMutation(DELETE_LEAGUE_TEAM);
  const [deleteLeagueTeamVenues] = useMutation(DELETE_LEAGUE_TEAM_VENUES);
  const [deleteLeagueAlternativeTeams] = useMutation(DELETE_LEAGUE_ALTERNATIVE_TEAMS);
  const [deleteLeagueCategory] = useMutation(SOFT_DELETE_LEAGUE_CATEGORY);

  const [updateTeamDetails] = useMutation(UPDATE_TEAM_DETAILS, {
    onCompleted: (data) => {
      setIsUpdating(false);
      isUpdatingRef.current = false;
      
      // Refresh the team details from the server
      if (teamData.team?.lt_id && teamData.league?.l_id) {
        // Update local state with the returned data
        if (data?.update_league_categories_by_pk) {
          const updated = data.update_league_categories_by_pk;
          setTeamDetails(updated);
          setTeamData((prev) => ({
            ...prev,
            alternateNames: updated.lc_alternate_name || [],
            brandName: updated.lc_brand_name || "",
            marketName: updated.lc_market_name || "",
          }));
          
          // Update conference name if conference was changed
          if (updated.lcf_id) {
            const newConf = leagueConferences.find(c => c.lcf_id === updated.lcf_id);
            if (newConf) {
              setCurrentConferenceName(newConf.lcf_name);
              setSelectedConferenceId(updated.lcf_id);
            }
          }
        }
        
        // Also refetch from server to ensure consistency
        setTimeout(() => {
          fetchTeamDetails();
        }, 300);
      }
      
      toast.success("Team details updated successfully!", { toastId: "update-success" });
      handleReset();
    },
    onError: (err) => {
      setIsUpdating(false);
      isUpdatingRef.current = false;
      toast.error("Error updating team details: " + err.message, { toastId: "update-error-static" });
    },
  });

  const [updateAlternateNames] = useMutation(UPDATE_TEAM_ALTERNATE_NAMES, {
    onCompleted: (data) => {
      if (data?.update_league_categories_by_pk && teamDetails) {
        const updated = data.update_league_categories_by_pk;
        setTeamDetails({
          ...teamDetails,
          lc_alternate_name: updated.lc_alternate_name || [],
        });
        setTeamData(prev => ({ ...prev, alternateNames: updated.lc_alternate_name || [] }));
      }
      setIsUpdating(false);
      isUpdatingRef.current = false;
      toast.success("Alternate names updated!", { toastId: "update-success" });
      handleReset();
    },
    onError: (err) => {
      setIsUpdating(false);
      isUpdatingRef.current = false;
      toast.error("Error updating alternate names: " + err.message, { toastId: "update-alternate-error" });
    },
  });

  const [updateBrandMarket] = useMutation(UPDATE_TEAM_BRAND_MARKET, {
    onCompleted: (data) => {
      if (data?.update_league_categories_by_pk && teamDetails) {
        const updated = data.update_league_categories_by_pk;
        setTeamDetails({
          ...teamDetails,
          lc_brand_name: updated.lc_brand_name || "",
          lc_market_name: updated.lc_market_name || "",
        });
        setTeamData(prev => ({ 
          ...prev, 
          brandName: updated.lc_brand_name || "", 
          marketName: updated.lc_market_name || "" 
        }));
      }
      setIsUpdating(false);
      isUpdatingRef.current = false;
      toast.success("Brand & Market names updated!", { toastId: "update-success" });
      handleReset();
    },
    onError: (err) => {
      setIsUpdating(false);
      isUpdatingRef.current = false;
      toast.error("Error updating brand/market: " + err.message, { toastId: "update-brand-market-error" });
    },
  });

  const [updateConference] = useMutation(UPDATE_TEAM_CONFERENCE, {
    onCompleted: (data) => {
      if (data?.update_league_categories_by_pk && teamDetails) {
        const updated = data.update_league_categories_by_pk;
        setTeamDetails({
          ...teamDetails,
          lcf_id: updated.lcf_id,
        });
        setSelectedConferenceId(updated.lcf_id || "");
        const newConf = leagueConferences.find(c => c.lcf_id === updated.lcf_id);
        if (newConf) setCurrentConferenceName(newConf.lcf_name);
      }
      setIsUpdating(false);
      isUpdatingRef.current = false;
      toast.success("Conference updated!", { toastId: "update-success" });
      handleReset();
    },
    onError: (err) => {
      setIsUpdating(false);
      isUpdatingRef.current = false;
      toast.error("Error updating conference: " + err.message, { toastId: "update-conference-error" });
    },
  });

  const [updateLeagueTeamConference] = useMutation(UPDATE_LEAGUE_TEAM_CONFERENCE, {
    onCompleted: (data) => {
      setIsAssigning(false);
      toast.success(`Team "${data.update_league_teams_by_pk.lt_name}" assigned successfully!`, { 
        toastId: "team-assignment-success" 
      });
      setSelectedTeamForAssignment(null);
      setSelectedConferenceForAssignment("");
      if (assignmentLeague?.l_id) {
        fetchAssignmentTeams(assignmentLeague.l_id);
      }
    },
    onError: (err) => {
      setIsAssigning(false);
      toast.error("Error assigning team: " + err.message, { toastId: "team-assignment-error" });
    },
  });

  const [getTeamsWithConference] = useLazyQuery(GET_TEAMS_WITH_CONFERENCE, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setAssignmentTeams(data?.league_teams || []);
      setAssignmentLoading(false);
    },
    onError: () => {
      toast.error("Error fetching teams with conference", { toastId: "fetch-teams-conf-error" });
      setAssignmentLoading(false);
    },
  });

  const [getAssignmentConferences] = useLazyQuery(GET_LEAGUE_CONFERENCES, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setAssignmentConferences(data?.league_conferences || []);
    },
    onError: () => {
      toast.error("Error fetching conferences", { toastId: "fetch-assignment-conf-error" });
    },
  });

  // Effects
  useEffect(() => {
    if (leaguesFetched.current) return;
    leaguesFetched.current = true;
    setLoading(true);
    getAllLeagues();
  }, []);

  useEffect(() => {
    if (activeTab === "conferences") {
      if (confTabFetched.current) return;
      confTabFetched.current = true;
      setConfLoading(true);
      getAllConferencesForTab();
    } else {
      confTabFetched.current = false;
    }
  }, [activeTab]);

  useEffect(() => {
    if (teamData.league?.l_id && isNcaaLeague(teamData.league)) {
      setLoadingConferences(true);
      getLeagueConferencesQuery({
        variables: { l_id: teamData.league.l_id },
      });
    } else {
      setLeagueConferences([]);
      setLoadingConferences(false);
    }
  }, [teamData.league?.l_id]);

  useEffect(() => {
    if (teamData.league?.l_id) {
      getTeams({ variables: { l_id: teamData.league.l_id } });
      setTeamData((prev) => ({
        ...prev,
        team: null,
        alternateNames: [],
        brandName: "",
        marketName: "",
      }));
      setSelectedConferenceId("");
      setCurrentConferenceName("");
      setTeamConferenceId("");
      setTeamDetails(null);
    }
  }, [teamData.league?.l_id]);

  useEffect(() => {
    if (teamData.team?.lt_id && teamData.league?.l_id && isNcaaLeague(teamData.league)) {
      getTeamConference({
        variables: {
          l_id: teamData.league.l_id,
          lt_category_ref_id: teamData.team.lt_category_ref_id || "",
          teamName: teamData.team.lt_name,
        },
      });
    } else {
      setTeamConferenceId("");
      setSelectedConferenceId("");
      setCurrentConferenceName("");
    }
  }, [teamData.team?.lt_id, teamData.league?.l_id]);

  useEffect(() => {
    if (teamData.team?.lt_id && teamData.league?.l_id) {
      const timer = setTimeout(() => {
        fetchTeamDetails();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setTeamDetailsLoading(false);
      setTeamDetails(null);
    }
  }, [teamData.team?.lt_id, teamData.league?.l_id]);

  useEffect(() => {
    if (teamDetails?.lcf_id && leagueConferences.length > 0) {
      const found = leagueConferences.find((c) => c.lcf_id === teamDetails.lcf_id);
      setCurrentConferenceName(found ? found.lcf_name : "");
    } else {
      setCurrentConferenceName("");
    }
  }, [teamDetails?.lcf_id, leagueConferences]);

  const fetchTeamDetails = () => {
    if (!teamData.team?.lt_id || !teamData.league?.l_id) {
      console.warn("Cannot fetch team details: Missing team ID or league ID");
      setTeamDetailsLoading(false);
      return;
    }
    
    setTeamDetailsLoading(true);
    console.log("Fetching team details for team:", teamData.team.lt_id);
    
    getTeamDetails({
      variables: {
        l_id: teamData.league.l_id,
        lt_category_ref_id: teamData.team.lt_category_ref_id || "",
        teamName: teamData.team.lt_name,
      },
    });
  };

  const fetchAssignmentTeams = (leagueId: string) => {
    if (!leagueId) return;
    setAssignmentLoading(true);
    getTeamsWithConference({ variables: { l_id: leagueId } });
  };

  const fetchAssignmentConferences = (leagueId: string) => {
    if (!leagueId) return;
    getAssignmentConferences({ variables: { l_id: leagueId } });
  };

  const handleAssignmentLeagueChange = (league: League | null) => {
    setAssignmentLeague(league);
    setSelectedTeamForAssignment(null);
    setSelectedConferenceForAssignment("");
    if (league?.l_id) {
      fetchAssignmentTeams(league.l_id);
      if (isNcaaLeague(league)) {
        fetchAssignmentConferences(league.l_id);
      }
    } else {
      setAssignmentTeams([]);
      setAssignmentConferences([]);
    }
  };

  const handleAssignTeamToConference = async () => {
    if (!selectedTeamForAssignment) {
      toast.error("Please select a team", { toastId: "no-team-selected" });
      return;
    }

    if (!selectedTeamForAssignment.lt_id) {
      toast.error("Team ID is missing", { toastId: "no-team-id" });
      return;
    }

    if (selectedTeamForAssignment.lcf_id === selectedConferenceForAssignment) {
      toast.error("Team is already assigned to this conference", { toastId: "already-assigned" });
      return;
    }

    setIsAssigning(true);
    try {
      await updateLeagueTeamConference({
        variables: {
          lt_id: selectedTeamForAssignment.lt_id,
          lcf_id: selectedConferenceForAssignment || null,
        },
      });

      if (teamData.team?.lt_id === selectedTeamForAssignment.lt_id) {
        await getTeamConference({
          variables: {
            l_id: selectedTeamForAssignment.l_id,
            lt_category_ref_id: selectedTeamForAssignment.lt_category_ref_id || "",
            teamName: selectedTeamForAssignment.lt_name,
          },
        });
        await fetchTeamDetails();
        setSelectedConferenceId(selectedConferenceForAssignment); 
        const newConf = leagueConferences.find(c => c.lcf_id === selectedConferenceForAssignment);
        if (newConf) {
          setCurrentConferenceName(newConf.lcf_name);
        } else {
          setCurrentConferenceName("");
        }
      }

      if (assignmentLeague?.l_id) {
        fetchAssignmentTeams(assignmentLeague.l_id);
      }
    } catch (err) {
      console.error("Assignment error:", err);
      toast.error("Failed to assign team to conference", { toastId: "assignment-failed" });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleLeagueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLeague = leagues.find((l) => l.l_id === e.target.value) || null;
    setTeamData((prev) => ({ ...prev, league: selectedLeague }));
  };

  const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTeam = teams.find((t) => t.lt_id === e.target.value) || null;
    setTeamData((prev) => ({ ...prev, team: selectedTeam }));
    setTeamConferenceId("");
    setSelectedConferenceId("");
    setCurrentConferenceName("");
    setTeamDetails(null);
    setTeamDetailsLoading(true);
  };

  const handleConferenceChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedConferenceId(e.target.value);
  };

  const handleAddAlternateName = () => {
    if (teamData.newAlternateName.trim()) {
      setTeamData((prev) => ({
        ...prev,
        alternateNames: [...prev.alternateNames, prev.newAlternateName.trim()],
        newAlternateName: "",
      }));
    }
  };

  const handleRemoveAlternateName = (index: number) => {
    setTeamData((prev) => ({
      ...prev,
      alternateNames: prev.alternateNames.filter((_, i) => i !== index),
    }));
  };

  const groupedConferences = React.useMemo(() => {
    const groups = new Map<string, { lcf_name: string; leagues: { l_id: string; l_name: string; lcf_id: string; lcf_deleted_at?: string | null }[] }>();
    conferences.forEach((conf) => {
      const key = conf.lcf_name.toLowerCase();
      if (!groups.has(key)) {
        groups.set(key, { lcf_name: conf.lcf_name, leagues: [] });
      }
      groups.get(key)!.leagues.push({
        l_id: conf.l_id,
        l_name: leagues.find((l) => l.l_id === conf.l_id)?.l_name || conf.l_id,
        lcf_id: conf.lcf_id,
        lcf_deleted_at: conf.lcf_deleted_at,
      });
    });
    return Array.from(groups.values());
  }, [conferences, leagues]);

  const filteredGroups = groupedConferences.filter((group) =>
    group.lcf_name.toLowerCase().includes(searchText.toLowerCase())
  );

  
const handleRemoveLeagueFromConference = async (
  conferenceName: string,
  leagueLcfId: string,
  leagueName: string
) => {
  try {
    await deleteConferenceMutation({ variables: { lcf_id: leagueLcfId } });
    toast.success(`Removed ${leagueName} from ${conferenceName}`);
    getConferences(); // ✅ Refreshes data – no page reload
  } catch (err) {
    toast.error("Failed to remove league from conference");
  }
};

const handleConfDelete = async (group: { lcf_name: string; leagues: { lcf_id: string }[] }) => {
  for (const row of group.leagues) {
    await deleteConferenceMutation({ variables: { lcf_id: row.lcf_id } });
  }
  toast.success(`Conference "${group.lcf_name}" deleted permanently.`);
  getConferences(); // ✅ Refreshes data – no page reload
  // Also refresh teams if needed
  if (teamData.league?.l_id) {
    getTeams({ variables: { l_id: teamData.league.l_id } });
    if (teamData.team?.lt_id) fetchTeamDetails();
  }
};

  const handleConfEditStart = (group: { lcf_name: string; leagues: { l_id: string; l_name: string; lcf_id: string }[] }) => {
    setEditingConfId(group.lcf_name);
    setEditingConfName(group.lcf_name);
    const currentLeagueIds = group.leagues.map((l) => l.l_id);
    setEditingConfLeagues(currentLeagueIds);
    setOriginalLeaguesForEditing(currentLeagueIds);
    setOpenLeagueDropdownFor(null);
  };

  const handleConfEditSave = async () => {
    if (!editingConfId) return;
    if (!editingConfName.trim()) {
      toast.error("Conference name is required", { toastId: "conf-name-required" });
      return;
    }

    const originalGroup = groupedConferences.find((g) => g.lcf_name === editingConfId);
    if (!originalGroup) return;

    const originalLeagueIds = originalGroup.leagues.map((l) => l.l_id);
    const newLeagueIds = editingConfLeagues;
    const nameChanged = editingConfName.trim() !== originalGroup.lcf_name;

    // Filter out leagues that are already in the original list (these would cause duplicates)
    const toAdd = newLeagueIds.filter((id) => !originalLeagueIds.includes(id));
    
    if (toAdd.length === 0 && !nameChanged) {
      toast.info("No changes to save", { toastId: "no-changes-to-save" });
      setEditingConfId(null);
      return;
    }

    const normalizedName = editingConfName.trim().toLowerCase();
    const duplicateLeagueIds = toAdd.filter((leagueId) =>
      conferences.some(
        (conf) =>
          conf.l_id === leagueId &&
          conf.lcf_name?.trim().toLowerCase() === normalizedName
      )
    );

    if (duplicateLeagueIds.length > 0) {
      const leagueNames = duplicateLeagueIds
        .map((leagueId) => leagues.find((l) => l.l_id === leagueId)?.l_name || leagueId)
        .join(", ");
      toast.error(
        `Cannot add conference to ${leagueNames}: league already has this conference.`,
        { toastId: "conf-edit-duplicate-error" }
      );
      return;
    }

    if (nameChanged) {
      for (const league of originalGroup.leagues) {
        try {
          await updateConferenceMutation({
            variables: {
              lcf_id: league.lcf_id,
              lcf_name: editingConfName.trim(),
              lcf_display_order: 0,
            },
          });
        } catch (err) {
          console.error("Failed to update conference name", err);
          toast.error(`Failed to update name for league ${league.l_name}`, { toastId: `name-upd-fail-${league.lcf_id}` });
          return;
        }
      }
    }

    for (const leagueId of toAdd) {
      try {
        await addConferenceMutation({
          variables: {
            l_id: leagueId,
            lcf_name: editingConfName.trim(),
            lcf_display_order: 0,
          },
        });
      } catch (err) {
        console.error("Failed to add conference to league", err);
        toast.error(`Failed to add conference to league ${leagueId}`, { toastId: `add-conf-fail-${leagueId}` });
        return;
      }
    }

    const toRemove = originalLeagueIds.filter((id) => !newLeagueIds.includes(id));
    for (const leagueId of toRemove) {
      const row = originalGroup.leagues.find((l) => l.l_id === leagueId);
      if (row) {
        try {
          await deleteConferenceMutation({ variables: { lcf_id: row.lcf_id } });
        } catch (err) {
          console.error("Failed to delete conference association", err);
          toast.error(`Failed to remove conference from league ${leagueId}`, { toastId: `delete-fail-${row.lcf_id}` });
          return;
        }
      }
    }

    toast.success("Conference updated successfully!", { toastId: "conf-edit-success" });
    setEditingConfId(null);
    getConferences();
  };

// const handleConfDelete = async (group: { lcf_name: string; leagues: { lcf_id: string; l_id: string; l_name: string }[] }) => {
//   for (const row of group.leagues) {
//     try {
//       await deleteConferenceMutation({ variables: { lcf_id: row.lcf_id } });
//     } catch (err) {
//       console.error("Failed to delete conference row", err);
//       toast.error(`Failed to delete from league ${row.l_name}`, { toastId: `delete-row-fail-${row.lcf_id}` });
//       return;
//     }
//   }
//   toast.success(`Conference "${group.lcf_name}" deleted from all leagues.`, { toastId: "conf-delete-group" });
//   getConferences();

//   if (teamData.league?.l_id) {
//     getTeams({ variables: { l_id: teamData.league.l_id } });
//     if (teamData.team?.lt_id) fetchTeamDetails();
//   }
// };
  const handleAddTeamFormChange = (field: keyof AddTeamForm, value: string | League | null) => {
    setAddTeamForm((prev) => {
      const updated = { ...prev, [field]: value } as AddTeamForm;
      if (field === "league") updated.conference = "";
      return updated;
    });
  };

  const handleAddTeamReset = () => {
    setAddTeamForm({
      league: null,
      teamName: "",
      teamColor: "#6366f1",
      abbreviation: "",
      conference: "",
    });
    setAddTeamConferences([]);
  };

  const handleConfAdd = () => {
    if (!newConfName.trim()) {
      toast.error("Conference name is required", { toastId: "conf-name-required-add" });
      return;
    }
    if (!newConfLeagueId) {
      toast.error("Please select a league", { toastId: "no-league-for-conf" });
      return;
    }

    const normalizedName = newConfName.trim().toLowerCase();
    const duplicateExists = conferences.some(
      (conf) => conf.l_id === newConfLeagueId && conf.lcf_name?.trim().toLowerCase() === normalizedName
    );

    if (duplicateExists) {
      toast.error("Conference already exists for this league.", { toastId: "conf-add-duplicate-local" });
      return;
    }

    addConferenceMutation({
      variables: {
        l_id: newConfLeagueId,
        lcf_name: newConfName.trim(),
        lcf_display_order: newConfOrder,
      },
    });
    setShowAddConfForm(false);
    setNewConfName("");
    setNewConfLeagueId("");
    setNewConfOrder(1);
  };

  const handleReset = () => {
    setTeamData((prev) => ({
      ...prev,
      newAlternateName: "",
      newBrandName: "",
      newMarketName: "",
    }));
  };
const handleUpdate = async () => {
  if (!teamData.team) {
    toast.error("Please select a team first", { toastId: "no-team-error" });
    return;
  }

  if (isUpdatingRef.current) return;
  isUpdatingRef.current = true;
  setIsUpdating(true);

  try {
    let currentLcId = teamDetails?.lc_id;

    if (!currentLcId) {
      console.log("No existing league_categories record. Creating one...");
      
      let categoryRefId = teamData.team.lt_category_ref_id;
      if (!categoryRefId) {
        categoryRefId = uuidv4();
        toast.info("loading...", { toastId: "gen-ref" });
      }

      const initialAlternateNames = teamData.alternateNames.length > 0 
        ? teamData.alternateNames 
        : [];
      const initialBrandName = teamData.newBrandName || "";
      const initialMarketName = teamData.newMarketName || "";
      const initialConferenceId = (isNcaaLeague(teamData.league) && selectedConferenceId) 
        ? selectedConferenceId 
        : null;

      const { data: insertData } = await insertLeagueCategory({
        variables: {
          l_id: teamData.league!.l_id,
          lc_name: teamData.team.lt_name,
          lc_category_ref_id: categoryRefId,
          lcf_id: initialConferenceId,
          lc_alternate_name: initialAlternateNames,
          lc_brand_name: initialBrandName,
          lc_market_name: initialMarketName,
          lc_colour_code: teamData.team.lt_color_code || "#6366f1",
          lc_abbrev: teamData.team.lt_abbreviation || "",
        },
      });

      currentLcId = insertData?.insert_league_categories_one?.lc_id;
      if (!currentLcId) throw new Error("Failed to create league_categories record");

      await fetchTeamDetails();
      toast.success("Team details initialized. Now saving your changes...", { toastId: "init-success" });
      
      setIsUpdating(false);
      isUpdatingRef.current = false;
      return; 
    }

    const currentDetails = teamDetails!;
    const currentAlternateNames = currentDetails.lc_alternate_name || [];
    const currentBrandName = currentDetails.lc_brand_name || "";
    const currentMarketName = currentDetails.lc_market_name || "";

    // Determine which fields have changed
    const finalAlternateNames = teamData.newAlternateName.trim()
      ? [...currentAlternateNames, teamData.newAlternateName.trim()]
      : teamData.alternateNames;
    
    const isAlternateNamesChanged = 
      JSON.stringify(finalAlternateNames) !== JSON.stringify(currentAlternateNames);
    const isBrandNameChanged = 
      teamData.newBrandName !== currentBrandName && teamData.newBrandName !== "";
    const isMarketNameChanged = 
      teamData.newMarketName !== currentMarketName && teamData.newMarketName !== "";
    const isConferenceChanged = 
      isNcaaLeague(teamData.league) && 
      selectedConferenceId !== (currentDetails.lcf_id || "");

    if (!isAlternateNamesChanged && !isBrandNameChanged && !isMarketNameChanged && !isConferenceChanged) {
      toast.error("No changes detected", { toastId: "no-changes" });
      setIsUpdating(false);
      isUpdatingRef.current = false;
      return;
    }

    const promises = [];

    if (isAlternateNamesChanged) {
      promises.push(
        updateAlternateNames({
          variables: {
            lc_id: currentDetails.lc_id,
            lc_alternate_name: finalAlternateNames,
          },
        })
      );
    }

    if (isBrandNameChanged || isMarketNameChanged) {
      promises.push(
        updateBrandMarket({
          variables: {
            lc_id: currentDetails.lc_id,
            lc_brand_name: isBrandNameChanged ? teamData.newBrandName : currentBrandName,
            lc_market_name: isMarketNameChanged ? teamData.newMarketName : currentMarketName,
          },
        })
      );
    }

    if (isConferenceChanged && isNcaaLeague(teamData.league)) {
      promises.push(
        updateConference({
          variables: {
            lc_id: currentDetails.lc_id,
            lcf_id: selectedConferenceId || null,
          },
        })
      );
    }

    await Promise.all(promises);
    toast.success("Team details updated successfully!", { toastId: "update-success" });
    handleReset();
    
    // Refresh the details to reflect latest changes
    await fetchTeamDetails();

  } catch (err: any) {
    console.error("❌ Update Error:", err);
    toast.error("Update failed: " + err.message, { toastId: "update-error" });
  } finally {
    setIsUpdating(false);
    isUpdatingRef.current = false;
  }
};

const handleDeleteTeam = () => {
  if (!teamData.team) {
    toast.error("Please select a team to delete", {
      toastId: "delete-no-team",
    });
    return;
  }

  const teamToDelete = teamData.team;
  const teamName = teamToDelete.lt_name;
  const teamId = teamToDelete.lt_id;
  const categoryRefId = teamToDelete.lt_category_ref_id;
  const leagueId = teamData.league?.l_id;

  const toastId = `delete-team-confirm-${teamId}`;

  const confirmDelete = async () => {
    setIsUpdating(true);
    isUpdatingRef.current = true;

    try {
      // Delete related records first
      await Promise.all([
        deleteLeagueTeamVenues({
          variables: { lt_id: teamId },
        }),
        deleteLeagueAlternativeTeams({
          variables: { lt_id: teamId },
        }),
      ]);

      // Delete team
      await deleteLeagueTeam({
        variables: { lt_id: teamId },
      });

      // Delete league_category (required)
      if (categoryRefId) {
        await deleteLeagueCategory({
          variables: {
            lc_category_ref_id: categoryRefId,
          },
        });
      }

      // Success message
      toast.success(`Team "${teamName}" deleted successfully!`, {
        toastId: "delete-team-success",
      });

      // Reset states
      setTeamDetails(null);
      setSelectedConferenceId("");
      setCurrentConferenceName("");
      setTeamConferenceId("");

      setTeamData((prev) => ({
        ...prev,
        team: null,
        alternateNames: [],
        brandName: "",
        marketName: "",
        newAlternateName: "",
        newBrandName: "",
        newMarketName: "",
      }));

      // Refresh teams list
      if (leagueId) {
        getTeams({
          variables: { l_id: leagueId },
        });
      }
    } catch (err: any) {
      console.error("❌ Delete Error:", err);

      const message = err?.message || "Unknown error";

      if (
        message.toLowerCase().includes("foreign key") ||
        message.toLowerCase().includes("violates")
      ) {
        toast.error(
          "Failed to delete team because linked records still exist. Please clear the linked data and try again.",
          {
            toastId: "delete-team-linked-error",
          }
        );
      } else {
        toast.error("Failed to delete team: " + message, {
          toastId: "delete-team-error",
        });
      }
    } finally {
      setIsUpdating(false);
      isUpdatingRef.current = false;
      toast.dismiss(toastId);
    }
  };

  toast.warn(
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: "420px" }}>
        Delete team "{teamName}"? This action cannot be undone.
      </div>

      <div
        style={{
          display: "flex",
          gap: "10px",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <button
          style={{
            background: "#ef4444",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "8px 14px",
            cursor: "pointer",
            minWidth: "100px",
          }}
          type="button"
          onClick={confirmDelete}
        >
          Confirm
        </button>

        <button
          style={{
            background: "#e2e8f0",
            color: "#1f2937",
            border: "none",
            borderRadius: "8px",
            padding: "8px 14px",
            cursor: "pointer",
            minWidth: "100px",
          }}
          type="button"
          onClick={() => toast.dismiss(toastId)}
        >
          Cancel
        </button>
      </div>
    </div>,
    {
      toastId,
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
      position: "top-center",
    }
  );
};

  const handleRefresh = () => window.location.reload();

  const getContrastColor = (hexColor: string): string => {
    const color = hexColor.replace("#", "");
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  const isCurrentLeagueNcaa = isNcaaLeague(teamData.league);
  const ncaaLeagues = leagues.filter(isNcaaLeague);

  return (
    <div className={`${styles.container} ${isDarkMode ? styles.darkMode : ""}`}>
      {/* <ToastContainer position="top-right" autoClose={3000} closeOnClick pauseOnHover={false} /> */}

      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>MANAGE TEAM ALTERNATE NAMES, BRAND & MARKET</p>
          <h1 className={styles.title}>Team Update</h1>
        </div>
        <div className={styles.searchSection}>
          <button
            className={styles.addTeamBtn}
            onClick={() => {
              setShowAddTeamModal(true);
              setAddTeamConfLoading(true);
              getAddTeamConferences();
            }}
          >
            + Add Team
          </button>
          <button className={styles.iconBtn} onClick={handleRefresh}>
            <RefreshRoundedIcon />
          </button>
          <button className={styles.iconBtn} onClick={() => setIsDarkMode(!isDarkMode)}>
            {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </button>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.tabs}>
          <button
            className={activeTab === "categories" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("categories")}
          >
            League Categories
          </button>
          <button
            className={activeTab === "conferences" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("conferences")}
          >
            League Conferences
          </button>
          
        </div>

        {/* Categories Tab */}
        {activeTab === "categories" && (
          <>
            <div className={styles.rowTwo}>
             <div className={styles.formGroup} ref={leagueDropdownRef}>
  <label>League</label>
  {loading && !leagues.length ? (
    <CircularProgress size={24} />
  ) : (
    <div className={styles.customSelect}>
      <div
        className={styles.selectTrigger}
        onClick={() => setIsLeagueDropdownOpen(!isLeagueDropdownOpen)}
      >
        {teamData.league?.l_name || "Select League"}
        <span className={styles.dropdownArrow}>▼</span>
      </div>
      {isLeagueDropdownOpen && (
        <div className={styles.selectDropdown}>
          <input
            type="text"
            placeholder="Search league..."
            value={leagueSearchTerm}
            onChange={(e) => setLeagueSearchTerm(e.target.value)}
            className={styles.searchInput}
            onClick={(e) => e.stopPropagation()}
          />
          <div className={styles.optionsList}>
            {filteredLeagues.length === 0 ? (
              <div className={styles.noOptions}>No leagues found</div>
            ) : (
              filteredLeagues.map((league) => (
                <div
                  key={league.l_id}
                  className={`${styles.option} ${teamData.league?.l_id === league.l_id ? styles.optionSelected : ""}`}
                  onClick={() => {
                    setTeamData((prev) => ({ ...prev, league }));
                    setIsLeagueDropdownOpen(false);
                    setLeagueSearchTerm("");
                    // Reset team and related data (same as handleLeagueChange)
                    setTeamData((prev) => ({ ...prev, team: null }));
                    setTeams([]);
                    setTeamDetails(null);
                    setSelectedConferenceId("");
                    setCurrentConferenceName("");
                    setTeamConferenceId("");
                  }}
                >
                  {league.l_name}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )}
</div>
<div className={styles.formGroup} ref={teamDropdownRef}>
  <label>Team Name</label>
  <div className={styles.customSelect}>
    <div
      className={styles.selectTrigger}
      onClick={() => setIsTeamDropdownOpen(!isTeamDropdownOpen)}
      style={{
        color: teamData.team?.lt_color_code || "inherit",
        fontWeight: teamData.team?.lt_color_code ? "600" : "normal",
      }}
    >
      {teamData.team?.lt_name || "Select Team"}
      <span className={styles.dropdownArrow}>▼</span>
    </div>
    {isTeamDropdownOpen && (
      <div className={styles.selectDropdown}>
        <input
          type="text"
          placeholder="Search team..."
          value={teamSearchTerm}
          onChange={(e) => setTeamSearchTerm(e.target.value)}
          className={styles.searchInput}
          onClick={(e) => e.stopPropagation()}
        />
        <div className={styles.optionsList}>
          {filteredTeams.length === 0 ? (
            <div className={styles.noOptions}>No teams found</div>
          ) : (
            filteredTeams.map((team) => (
              <div
                key={team.lt_id}
                className={`${styles.option} ${teamData.team?.lt_id === team.lt_id ? styles.optionSelected : ""}`}
                onClick={() => {
                  setTeamData((prev) => ({ ...prev, team }));
                  setIsTeamDropdownOpen(false);
                  setTeamSearchTerm("");
                  // Reset related states (same as handleTeamChange)
                  setTeamConferenceId("");
                  setSelectedConferenceId("");
                  setCurrentConferenceName("");
                  setTeamDetails(null);
                  setTeamDetailsLoading(true);
                }}
                style={{
                  color: team.lt_color_code || "inherit",
                  fontWeight: team.lt_color_code ? "600" : "normal",
                }}
              >
                {team.lt_name}
              </div>
            ))
          )}
        </div>
      </div>
    )}
  </div>
</div>
          <div className={styles.formGroup} ref={confDropdownRef}>
  <label>Conference</label>
  {loadingConferences ? (
    <CircularProgress size={24} />
  ) : leagueConferences.length === 0 ? (
    <div className={styles.emptyState}>No conferences available for selected league</div>
  ) : (
    <div className={styles.customSelect}>
      <div
        className={styles.selectTrigger}
        onClick={() => isCurrentLeagueNcaa && setIsConfDropdownOpen(!isConfDropdownOpen)}
        style={{ opacity: isCurrentLeagueNcaa ? 1 : 0.6, cursor: isCurrentLeagueNcaa ? "pointer" : "not-allowed" }}
      >
        {leagueConferences.find(c => c.lcf_id === selectedConferenceId)?.lcf_name || "Select Conference"}
        <span className={styles.dropdownArrow}>▼</span>
      </div>
      {isConfDropdownOpen && isCurrentLeagueNcaa && (
        <div className={styles.selectDropdown}>
          <input
            type="text"
            placeholder="Search conference..."
            value={confSearchTerm}
            onChange={(e) => setConfSearchTerm(e.target.value)}
            className={styles.searchInput}
            onClick={(e) => e.stopPropagation()}
          />
          <div className={styles.optionsList}>
            {leagueConferences.filter(conf =>
              conf.lcf_name.toLowerCase().includes(confSearchTerm.toLowerCase())
            ).length === 0 ? (
              <div className={styles.noOptions}>No conferences found</div>
            ) : (
              leagueConferences
                .filter(conf => conf.lcf_name.toLowerCase().includes(confSearchTerm.toLowerCase()))
                .map((conf) => (
                  <div
                    key={conf.lcf_id}
                    className={`${styles.option} ${selectedConferenceId === conf.lcf_id ? styles.optionSelected : ""}`}
                    onClick={() => {
                      setSelectedConferenceId(conf.lcf_id);
                      setIsConfDropdownOpen(false);
                      setConfSearchTerm("");
                    }}
                  >
                    {conf.lcf_name}
                  </div>
                ))
            )}
          </div>
        </div>
      )}
    </div>
  )}
</div>
            </div>

            {isCurrentLeagueNcaa && teamData.league && teamData.team && (
              <>
                <div className={styles.comparisonContainer}>
                  <div className={styles.comparisonItem}>
                    <div className={styles.comparisonLabel}>Current Conference (Existing)</div>
                    <div className={styles.comparisonValue}>
                      {currentConferenceName ? (
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {currentConferenceName}
                          <span style={{ fontSize: "11px", background: "#dbeafe", color: "#1e40af", padding: "2px 8px", borderRadius: "12px" }}>
                            {teamData.league.l_name}
                          </span>
                        </span>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                  <div className={styles.comparisonItem}>
                    <div className={styles.comparisonLabel}>New Conference (After Update)</div>
                    <div className={styles.comparisonValue}>
                      {selectedConferenceId && selectedConferenceId !== teamConferenceId ? (
                        (() => {
                            const selectedConf = leagueConferences.find((c) => c.lcf_id === selectedConferenceId);
                            return selectedConf ? (
                              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {selectedConf.lcf_name}
                                <span style={{ fontSize: "11px", background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: "12px" }}>
                                  {teamData.league.l_name}
                                </span>
                              </span>
                            ) : (
                              "Select a conference"
                            );
                          })()
                        ) : selectedConferenceId === teamConferenceId && teamConferenceId ? (
                          <span style={{ color: "#64748b" }}>Same as current conference</span>
                        ) : (
                          "Select a conference from dropdown"
                        )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {teamData.league && teamData.team && (
              <>
                <div className={styles.rowThree}>
                  <div>
                    <p className={styles.sectionTitle}>EXISTING ALTERNATE NAMES</p>
                    <div className={styles.tagsBox}>
                      {teamData.alternateNames.length > 0 ? (
                        teamData.alternateNames.map((name, idx) => (
                          <div key={idx} className={styles.tag}>
                            {name}
                            <span onClick={() => handleRemoveAlternateName(idx)}>✕</span>
                          </div>
                        ))
                      ) : (
                        <span style={{ color: "#94a3b8" }}>No alternate names</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className={styles.sectionTitle}>EXISTING BRAND NAME</p>
                    <div className={styles.readOnly}>{teamData.brandName || "—"}</div>
                  </div>
                  <div>
                    <p className={styles.sectionTitle}>EXISTING MARKET NAME</p>
                    <div className={styles.readOnly}>{teamData.marketName || "—"}</div>
                  </div>
                </div>

                <div className={styles.rowThree}>
                  <div className={styles.formGroup}>
                    <label>Alternate Name</label>
                    <input
                      type="text"
                      value={teamData.newAlternateName}
                      onChange={(e) => setTeamData({ ...teamData, newAlternateName: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && handleAddAlternateName()}
                      placeholder="Enter alternate name..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Brand Name</label>
                    <input
                      type="text"
                      value={teamData.newBrandName}
                      onChange={(e) => setTeamData({ ...teamData, newBrandName: e.target.value })}
                      placeholder="Enter brand name..."
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Market Name</label>
                    <input
                      type="text"
                      value={teamData.newMarketName}
                      onChange={(e) => setTeamData({ ...teamData, newMarketName: e.target.value })}
                      placeholder="Enter market name..."
                    />
                  </div>
                </div>

                {isCurrentLeagueNcaa && (
                  <div className={styles.comparisonNote}>
                    Note: Updating the conference will move this team from the current conference to the selected new
                    conference under the same league.
                  </div>
                )}

                <div className={styles.buttonRow}>
                  <button className={styles.resetBtn} onClick={handleReset} disabled={isUpdating}>
                    Reset
                  </button>
                  <button
                    className={styles.deleteBtn}
                    onClick={handleDeleteTeam}
                    disabled={isUpdating || !teamData.team}
                    type="button"
                  >
                    {isUpdating ? "Processing..." : "Delete"}
                  </button>
                  <button 
                    className={styles.updateBtn} 
                    onClick={handleUpdate} 
                    disabled={isUpdating || !teamData.team || (teamDetailsLoading && !teamDetails?.lc_id)}
                  >
                    {isUpdating ? "Updating..." : teamDetailsLoading ? "Loading..." : "Update"}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* Conferences Tab */}
        {activeTab === "conferences" && (
          <div className={styles.confSection}>
            <div className={styles.confHeader}>
              <div className={styles.confHeaderTitle}>All Conferences (grouped by name)</div>
              <div>
                <div className={styles.filterRow}>
                  <input
                    type="text"
                    placeholder="Search conference..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className={styles.searchInput}
                    disabled={groupedConferences.length === 0}
                  />
                  {canEditConferences && (
                    <button
                      className={styles.addConfBtn}
                      onClick={() => {
                        setShowAddConfForm(true);
                        setNewConfName("");
                        setNewConfLeagueId("");
                        setNewConfOrder(1);
                      }}
                    >
                      + Add Conference
                    </button>
                  )}
                </div>
              </div>
            </div>

            {confLoading ? (
              <div style={{ textAlign: "center", padding: "24px" }}>
                <CircularProgress size={24} />
              </div>
            ) : (
              <table className={styles.confTable}>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>Conference Name</th>
                    <th>Leagues</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {showAddConfForm && (
                    <tr>
                      <td>—</td>
                      <td>
                        <input
                          className={styles.tableInput}
                          type="text"
                          value={newConfName}
                          onChange={(e) => setNewConfName(e.target.value)}
                          placeholder="Conference name..."
                        />
                       </td>
                      <td>
                        <select
                          className={styles.tableSelect}
                          value={newConfLeagueId}
                          onChange={(e) => setNewConfLeagueId(e.target.value)}
                        >
                          <option value="">Select a league</option>
                          {ncaaLeagues.map((league) => (
                            <option key={league.l_id} value={league.l_id}>
                              {league.l_name}
                            </option>
                          ))}
                        </select>
                       </td>
                      <td>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button className={styles.saveBtn} onClick={handleConfAdd}>Save</button>
                          <button className={styles.cancelBtn} onClick={() => setShowAddConfForm(false)}>Cancel</button>
                        </div>
                       </td>
                    </tr>
                  )}

                  {filteredGroups.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={styles.confEmpty}>No conferences found</td>
                    </tr>
                  ) : (
                    filteredGroups.map((group, idx) => (
                      <tr key={group.lcf_name}>
                        <td>{idx + 1}</td>
                        <td>
                          {editingConfId === group.lcf_name ? (
                            <input
                              className={styles.confEditInput}
                              value={editingConfName}
                              onChange={(e) => setEditingConfName(e.target.value)}
                              autoFocus
                            />
                          ) : (
                            group.lcf_name
                          )}
                        </td>
                        <td>
                          {editingConfId === group.lcf_name ? (
                            <div className={styles.customMultiSelect}>
                              <div
                                className={styles.dropdownHeader}
                                onClick={() => setOpenLeagueDropdownFor(openLeagueDropdownFor === group.lcf_name ? null : group.lcf_name)}
                              >
                                <div className={styles.selectedLeagues}>
                                  {editingConfLeagues.length === 0 && "Select leagues..."}
                                  {editingConfLeagues.slice(0, 2).map(id => {
                                    const league = leagues.find(l => l.l_id === id);
                                    return <span key={id} className={styles.selectedLeagueTag}>{league?.l_name}</span>;
                                  })}
                                  {editingConfLeagues.length > 2 && `+${editingConfLeagues.length - 2}`}
                                </div>
                                <span className={styles.dropdownArrow}>▼</span>
                              </div>
                              {openLeagueDropdownFor === group.lcf_name && (
                                <div className={styles.dropdownList}>
                                  {ncaaLeagues.map(league => {
                                    const isAlreadyInConference = originalLeaguesForEditing.includes(league.l_id);
                                    const isSelected = editingConfLeagues.includes(league.l_id);
                                    const normalizedName = editingConfName.trim().toLowerCase();
                                    const hasSameConferenceAlready = normalizedName.length > 0 && conferences.some(
                                      (conf) =>
                                        conf.l_id === league.l_id &&
                                        conf.lcf_name?.trim().toLowerCase() === normalizedName
                                    );
                                    const disableBecauseDuplicate = hasSameConferenceAlready && !isSelected && !isAlreadyInConference;

                                    return (
                                      <label 
                                        key={league.l_id} 
                                        className={`${styles.dropdownOption} ${(isAlreadyInConference && !isSelected) || disableBecauseDuplicate ? styles.disabledOption : ''}`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          disabled={(isAlreadyInConference && !isSelected) || disableBecauseDuplicate}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              if (!isAlreadyInConference && !disableBecauseDuplicate) {
                                                setEditingConfLeagues([...editingConfLeagues, league.l_id]);
                                              }
                                            } else {
                                              setEditingConfLeagues(editingConfLeagues.filter(id => id !== league.l_id));
                                            }
                                          }}
                                        />
                                        {league.l_name}
                                        {(isAlreadyInConference || disableBecauseDuplicate) && (
                                          <span className={styles.alreadyAddedBadge}>
                                            {disableBecauseDuplicate ? "already has this conference" : "already in this conference"}
                                          </span>
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={styles.leagueBadgeContainer}>
                              {group.leagues.map((league, i) => (
                                <span
                                  key={i}
                                  className={styles.leagueBadge}
                                  style={{ opacity: league.lcf_deleted_at ? 0.5 : 1 }}
                                >
                                  {league.l_name}
                                  {league.lcf_deleted_at && (
                                    <span style={{ marginLeft: 6, fontSize: 11, color: "#c0392b" }}>
                                      (Deleted)
                                    </span>
                                  )}
                                  {canEditConferences && (
                                    <span
                                      className={styles.badgeRemove}
                                      onClick={() => handleRemoveLeagueFromConference(group.lcf_name, league.lcf_id, league.l_name)}
                                      title={`Remove ${league.l_name} from ${group.lcf_name}`}
                                    >
                                      ✕
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          {canEditConferences ? (
                            <div className={styles.confActions}>
                              {editingConfId === group.lcf_name ? (
                                <>
                                  <button className={styles.confSaveBtn} onClick={handleConfEditSave}>Save</button>
                                  <button className={styles.confCancelBtn} onClick={() => setEditingConfId(null)}>Cancel</button>
                                </>
                              ) : (
                                <>
                                  <button className={styles.confEditBtn} onClick={() => handleConfEditStart(group)}>Edit</button>
                                  <button className={styles.confDeleteBtn} onClick={() => handleConfDelete(group)}>Delete</button>
                                </>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: 12 }}>View</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

    
      </div>

      {/* Add Team Modal */}
      {showAddTeamModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAddTeamModal(false)}>
          <div className={`${styles.modal} ${isDarkMode ? styles.darkModal : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Add Team</h2>
              <button className={styles.modalClose} onClick={() => setShowAddTeamModal(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>League</label>
                <select
                  value={addTeamForm.league?.l_id || ""}
                  onChange={(e) => {
                    const selected = leagues.find((l) => l.l_id === e.target.value) || null;
                    handleAddTeamFormChange("league", selected);
                  }}
                >
                  <option value="">Select League</option>
                  {leagues.map((league) => (
                    <option key={league.l_id} value={league.l_id}>{league.l_name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Team Name</label>
                <input
                  type="text"
                  value={addTeamForm.teamName}
                  onChange={(e) => handleAddTeamFormChange("teamName", e.target.value)}
                  placeholder="Enter team name..."
                />

                
              </div>
              <div className={styles.rowTwo}>
                <div className={styles.formGroup}>
                  <label>Abbreviation</label>
                  <input
                    type="text"
                    value={addTeamForm.abbreviation}
                    onChange={(e) => handleAddTeamFormChange("abbreviation", e.target.value)}
                    placeholder="e.g. LAL"
                    maxLength={10}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Team Color</label>
                  <div className={styles.colorInputWrapper}>
                    <input
                      type="color"
                      value={addTeamForm.teamColor}
                      onChange={(e) => handleAddTeamFormChange("teamColor", e.target.value)}
                    />
                    <span
                      className={styles.colorValue}
                      style={{
                        backgroundColor: addTeamForm.teamColor,
                        color: getContrastColor(addTeamForm.teamColor),
                      }}
                    >
                      {addTeamForm.teamColor.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
         {canEditConferences && isNcaaLeague(addTeamForm.league) && (
  <div className={styles.formGroup} ref={addTeamConfDropdownRef}>
    <label>Conference</label>
    {addTeamConfLoading ? (
      <CircularProgress size={20} />
    ) : (
      <div className={styles.customSelect}>
        <div
          className={styles.selectTrigger}
          onClick={() => setIsAddTeamConfDropdownOpen(!isAddTeamConfDropdownOpen)}
        >
          {addTeamConferences.find(c => c.lcf_id === addTeamForm.conference)?.lcf_name || "Select Conference"}
          <span className={styles.dropdownArrow}>▼</span>
        </div>
        {isAddTeamConfDropdownOpen && (
          <div className={styles.selectDropdown}>
            <input
              type="text"
              placeholder="Search conference..."
              value={addTeamConfSearchTerm}
              onChange={(e) => setAddTeamConfSearchTerm(e.target.value)}
              className={styles.searchInput}
              onClick={(e) => e.stopPropagation()}
            />
            <div className={styles.optionsList}>
              {addTeamConferences.filter(conf =>
                conf.lcf_name.toLowerCase().includes(addTeamConfSearchTerm.toLowerCase())
              ).length === 0 ? (
                <div className={styles.noOptions}>No conferences found</div>
              ) : (
                addTeamConferences
                  .filter(conf => conf.lcf_name.toLowerCase().includes(addTeamConfSearchTerm.toLowerCase()))
                  .map((conf) => {
                    const leagueName = leagues.find(l => l.l_id === conf.l_id)?.l_name || conf.l_id;
                    const isSameLeague = conf.l_id === addTeamForm.league?.l_id;
                    return (
                      <div
                        key={conf.lcf_id}
                        className={`${styles.option} ${addTeamForm.conference === conf.lcf_id ? styles.optionSelected : ""}`}
                        onClick={() => {
                          handleAddTeamFormChange("conference", conf.lcf_id);
                          setIsAddTeamConfDropdownOpen(false);
                          setAddTeamConfSearchTerm("");
                        }}
                      >
                        {conf.lcf_name}
                        {!isSameLeague && <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "6px" }}>({leagueName})</span>}
                        {isSameLeague && <span style={{ marginLeft: "6px" }}>★</span>}
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        )}
      </div>
    )}
    {/* copy conference box (unchanged) */}
    {(() => {
      const selectedConf = addTeamConferences.find(c => c.lcf_id === addTeamForm.conference);
      if (selectedConf && selectedConf.l_id !== addTeamForm.league?.l_id) {
        const fromLeague = leagues.find(l => l.l_id === selectedConf.l_id)?.l_name || "another league";
        return (
          <div className={styles.copyConfBox}>
            <span>
              "{selectedConf.lcf_name}" belongs to <strong>{fromLeague}</strong>. Add it to{" "}
              <strong>{addTeamForm.league?.l_name}</strong> as well?
            </span>
            <button className={styles.copyConfBtn} disabled={copyingConf} onClick={() => { /* same as before */ }}>
              {copyingConf ? "Adding..." : `Add to ${addTeamForm.league?.l_name}`}
            </button>
          </div>
        );
      }
      return null;
    })()}
    <p className={styles.conferenceNote}>
      Conference is required for NCAA leagues. ★ = same league
    </p>
  </div>
)}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.resetBtn} onClick={handleAddTeamReset}>Reset</button>
              <button className={styles.updateBtn} onClick={handleAddTeamSubmit}>Add Team</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamUpdate;

